import type { Option } from "../core/option.ts";
import { deepFrozen } from "../core/frozen-collections.ts";

/**
 * IngestionGate — the pause between a cell response resolving and its install
 * (project sheet-scroll-smoothness, candidate D).
 *
 * The request is never gated: the rendered range kicks its fetch the instant it
 * changes. What the gate delays is the write that follows the response — the
 * store batch, sync bookkeeping, formula recompute and the invalidation that
 * fans out to every rendered row — because on a dense calendar that install is
 * the work that lands inside motion frames while the user is still scrolling.
 * A held gate is a deferred install, never a dropped one: every waiter resolves
 * on release and every existing deadline is still measured from the last input.
 *
 * The SDK only awaits the gate — through a `Slot`, which owns the lifecycle
 * edges. Who holds it is the layer that knows about gestures (the sheet UI,
 * from the scroll engine's motion state); `OPEN` is the default everywhere and
 * never waits.
 */
export interface IngestionGate {
  /** True while installs must wait. */
  held(): boolean;
  /**
   * One consumer's wait: its promise resolves once installs may proceed
   * (immediately when not held); `cancel()` ends *this* consumer's wait
   * without touching anyone else's. A gate shared by several slots — the
   * sheet UI installs one gate on the cells accessor and the rows sheet —
   * therefore keeps polling exactly as long as somebody still waits.
   */
  whenReleased(): IngestionGate.Wait;
}

/** Types and values that go with `IngestionGate`: `Wait`, `OPEN`, `polled`, `Waiter`, `Slot`. */
export namespace IngestionGate {
  export interface Wait {
    readonly promise: Promise<void>;
    cancel(): void;
  }

  const RESOLVED: Wait = { promise: Promise.resolve(), cancel: () => {} };

  /** Never holds. The default for every sheet and accessor. */
  export const OPEN: IngestionGate = deepFrozen({
    held: () => false,
    whenReleased: () => RESOLVED,
  });

  /**
   * A gate that asks `held()` again on `schedule` until it says no. One poll
   * chain serves every waiter; each waiter is its own `Wait`, and the chain
   * ends when the gate releases or when the last waiter cancels. `schedule`
   * is `requestAnimationFrame` in the browser and a manual queue in tests.
   */
  export function polled(
    held: () => boolean,
    schedule: (tick: () => void) => void,
  ): IngestionGate {
    const waiters = new Set<() => void>();
    let chain = 0; // the live chain's token; a stale tick sees a different one
    const settleAll = (): void => {
      const resolves = [...waiters];
      waiters.clear();
      chain++;
      for (const r of resolves) r();
    };
    const startChain = (): void => {
      const mine = ++chain;
      const tick = (): void => {
        if (chain !== mine) return; // released or fully cancelled meanwhile
        if (held()) {
          schedule(tick);
          return;
        }
        settleAll();
      };
      schedule(tick);
    };
    return {
      held,
      whenReleased(): Wait {
        if (!held()) return RESOLVED;
        let resolve: () => void = () => {};
        const promise = new Promise<void>((r) => (resolve = r));
        const wasIdle = waiters.size === 0;
        waiters.add(resolve);
        if (wasIdle) startChain();
        return {
          promise,
          cancel: () => {
            if (!waiters.delete(resolve)) return;
            resolve();
            if (waiters.size === 0) chain++; // the last consumer left: the chain ends
          },
        };
      },
    };
  }

  /**
   * The gate an SDK object actually awaits. A slot has exactly one owner at a
   * time (the UI that installed its gate) and the guarantees a raw gate cannot
   * give: replacing the gate wakes every waiter parked on the old one, so an
   * outgoing sheet's response never stays retained behind an incoming sheet's
   * gesture — and this slot's wait on the old gate is cancelled, which is the
   * slot's own wait only: a gate shared with another slot keeps polling for
   * that slot; `release()` (dispose) does the same; `clear(owner)` only clears while `owner` is the installed
   * gate, so an older view detaching cannot strip a newer view's gate off a
   * shared model. Waiters share one *cycle* per hold: one promise, one
   * reaction on the gate, dropped when the cycle settles by release or by
   * replacement — a long-lived owner across many gestures accumulates nothing.
   * A waiter woken by replacement proceeds to its post-wait checks; it does
   * not re-queue behind the new owner.
   */
  /**
   * A slot as a waiter sees it: the gate it would wait on and the wait
   * itself. The same live `Slot`, typed without `set` / `clear` / `release`
   * (what `instrumentationOf(handle).ingestion` hands a test harness).
   */
  export type Waiter = Pick<Slot, "whenReleased" | "current">;

  export class Slot implements IngestionGate {
    #gate: IngestionGate = OPEN;
    #cycle: Option<{ wait: Wait; promise: Promise<void>; resolve: () => void }> = null;

    get current(): IngestionGate {
      return this.#gate;
    }

    /** True while a hold cycle is open (a waiter is parked). Test hook. */
    get waiting(): boolean {
      return this.#cycle != null;
    }

    set(gate: IngestionGate): void {
      if (gate === this.#gate) return;
      this.#gate = gate;
      this.#settle();
    }

    /** Owner-scoped: clears only while `owner` is the installed gate. */
    clear(owner: IngestionGate): void {
      if (this.#gate === owner) this.set(OPEN);
    }

    /** Drop the gate and wake everything parked on it (dispose). */
    release(): void {
      this.set(OPEN);
    }

    held(): boolean {
      return this.#gate.held();
    }

    whenReleased(): Wait {
      const gate = this.#gate;
      if (!gate.held()) return RESOLVED;
      if (this.#cycle != null) return { promise: this.#cycle.promise, cancel: () => {} };
      let resolve: () => void = () => {};
      const promise = new Promise<void>((r) => (resolve = r));
      const wait = gate.whenReleased();
      const cycle = { wait, promise, resolve };
      this.#cycle = cycle;
      wait.promise.then(() => {
        if (this.#cycle === cycle) this.#settle();
      });
      return { promise, cancel: () => {} };
    }

    /** End the open cycle: wake every SDK waiter and cancel this slot's wait on the gate. */
    #settle(): void {
      const c = this.#cycle;
      if (c == null) return;
      this.#cycle = null;
      c.wait.cancel();
      c.resolve();
    }
  }
}
